import { HashRouter as Router } from 'react-router-dom'

import AppRoutes from './routes'
import CommandPaletteHost from './components/command-palette/CommandPaletteHost'
import AchievementToast from './components/achievement-toast/AchievementToast'
import { AchievementsProvider } from './_hooks/achievements-context'
import { AuthProvider } from './_hooks/auth-context'
import { QuickPassProfileProvider } from './_hooks/quickpass-profile-context'
import { ImportedListingsProvider } from './_hooks/imported-listings-context'
import { QuickPassNotificationsProvider } from './_hooks/quickpass-notifications-context'
import { MessagesUnreadProvider } from './_hooks/messages-unread-context'
import { ResidentTenancyProvider } from './_hooks/resident-tenancy-context'
import { ResidentMessagesUnreadProvider } from './_hooks/resident-messages-unread-context'
import { ExperienceProvider } from './_hooks/experience-context'
import { ListingDecisionsProvider } from './_hooks/listing-decisions-context'
import { CelebrationProvider } from './_hooks/celebration-context'
import { ThemeProvider } from './_hooks/theme-context'
import AppMotionProvider from './components/app-motion-provider/AppMotionProvider'

/**
 * Root application shell. HashRouter is intentional — it powers both the
 * embed SDK takeover (`host.com/#/quickpass/*`) and the future Capacitor
 * wrap, which serves from `file://` and cannot use BrowserRouter.
 *
 * The provider stack (outermost → innermost):
 *   - ThemeProvider: light / cozy / dark mode applied to <html data-theme>
 *   - AppMotionProvider: app-wide Framer Motion defaults
 *   - AuthProvider: Firebase Auth state + resolved QuickPassUser
 *   - QuickPassProfileProvider: renter profile (preferences + saved listings)
 *   - ImportedListingsProvider: URL-imported listings + scrape state
 *   - QuickPassNotificationsProvider: passive-signal notifications + unread count
 *   - MessagesUnreadProvider: total unread message count for the nav badge
 *   - ResidentTenancyProvider: resident tenancies + selection + selected ledger
 *   - ExperienceProvider: active land (Search vs Resident) for the RBC switcher
 *   - ListingDecisionsProvider: per-listing tags + notes (localStorage)
 *   - CelebrationProvider: confetti trigger + overlay
 *   - AchievementsProvider: gamified milestone tracking + unlock toasts
 *   - Router: HashRouter
 *
 * @returns {JSX.Element}
 */
const App = (): JSX.Element => {
  return (
    <ThemeProvider>
      <AppMotionProvider>
        <AuthProvider>
          <QuickPassProfileProvider>
            <ImportedListingsProvider>
              <QuickPassNotificationsProvider>
                <MessagesUnreadProvider>
                  <ResidentTenancyProvider>
                    <ResidentMessagesUnreadProvider>
                      <ExperienceProvider>
                        <ListingDecisionsProvider>
                          <CelebrationProvider>
                            <AchievementsProvider>
                              <Router>
                                <AppRoutes />
                                <CommandPaletteHost />
                                <AchievementToast />
                              </Router>
                            </AchievementsProvider>
                          </CelebrationProvider>
                        </ListingDecisionsProvider>
                      </ExperienceProvider>
                    </ResidentMessagesUnreadProvider>
                  </ResidentTenancyProvider>
                </MessagesUnreadProvider>
              </QuickPassNotificationsProvider>
            </ImportedListingsProvider>
          </QuickPassProfileProvider>
        </AuthProvider>
      </AppMotionProvider>
    </ThemeProvider>
  )
}

export default App
